import {
  sequencesCreateMutation,
  sequencesListListQueryKey,
  sequencesListQueryKey,
  sequencesRetrieveOptions,
  sequencesRetrieveQueryKey,
  sequencesUpdateMutation
} from '@crm/api'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Form, Input, notification } from 'antd'
import { useEffect } from 'react'

type CampaignsFormProps = {
  id?: number
  formName: string
  onClose: () => void
}

export const CampaignsForm = ({
  id,
  onClose,
  formName
}: CampaignsFormProps) => {
  const { t } = useLocales()
  const [form] = Form.useForm()

  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { data } = useQuery({
    ...sequencesRetrieveOptions({ path: { id: Number(id) } }),
    enabled: !!id
  })

  useEffect(() => {
    if (data) {
      form.setFieldsValue(data)
    }
  }, [data])

  const { mutate: create } = useMutation({
    ...sequencesCreateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => onSuccessMutation()
  })

  const { mutate: edit } = useMutation({
    ...sequencesUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => onSuccessMutation()
  })

  const onSuccessMutation = () => {
    if (id) {
      invalidateQueries([
        sequencesRetrieveQueryKey({ path: { id: Number(id) } })
      ])
    }

    invalidateQueries([sequencesListQueryKey(), sequencesListListQueryKey()])
    onClose()
    notification.success({
      message: t('success.success'),
      description: t('success.success_data_save')
    })
  }

  const onSubmit = async () => {
    const values = await form.validateFields()

    return id
      ? edit({ body: values, path: { id: Number(id) } })
      : create({ body: values })
  }

  return (
    <Form name={formName} form={form} onFinish={onSubmit} layout="vertical">
      <Form.Item
        name="title"
        label={t('campaigns.name')}
        rules={[{ required: true }]}
      >
        <Input />
      </Form.Item>
    </Form>
  )
}
